import * as React from "react"

// MediaQueryList Event based useEventListener interface
function useEventListener<K extends keyof MediaQueryListEventMap>(
   eventName: K,
   handler: (event: MediaQueryListEventMap[K]) => void,
   element: React.RefObject<MediaQueryList>,
   options?: boolean | AddEventListenerOptions,
): void

// Window Event based useEventListener interface
function useEventListener<K extends keyof WindowEventMap>(
   eventName: K,
   handler: (event: WindowEventMap[K]) => void,
   element?: undefined,
   options?: boolean | AddEventListenerOptions,
): void

// Element Event based useEventListener interface
function useEventListener<
   K extends keyof HTMLElementEventMap & keyof SVGElementEventMap,
   T extends Element = K extends keyof HTMLElementEventMap
      ? HTMLDivElement
      : SVGElement,
>(
   eventName: K,
   handler:
      | ((event: HTMLElementEventMap[K]) => void)
      | ((event: SVGElementEventMap[K]) => void),
   element: React.RefObject<T>,
   options?: boolean | AddEventListenerOptions,
): void

// Document Event based useEventListener interface
function useEventListener<K extends keyof DocumentEventMap>(
   eventName: K,
   handler: (event: DocumentEventMap[K]) => void,
   element: React.RefObject<Document>,
   options?: boolean | AddEventListenerOptions,
): void

/**
 * Custom hook that attaches event listeners to DOM elements, the window, or media query lists.
 * @template KW - The type of event for window events.
 * @template KH - The type of event for HTML or SVG element events.
 * @template KM - The type of event for media query list events.
 * @template T - The type of the DOM element (default is `HTMLElement`).
 * @param {KW | KH | KM} eventName - The name of the event to listen for.
 * @param {(event: WindowEventMap[KW] | HTMLElementEventMap[KH] | SVGElementEventMap[KH] | MediaQueryListEventMap[KM] | Event) => void} handler - The event handler function.
 * @param {RefObject<T>} [element] - The DOM element or media query list to attach the event listener to (optional).
 * @param {boolean | AddEventListenerOptions} [options] - An options object that specifies characteristics about the event listener (optional).
 * @public
 * @see [Documentation](https://usehooks-ts.com/react-hook/use-event-listener)
 * @example
 * ```tsx
 * // Example 1: Attach a window event listener
 * useEventListener('resize', handleResize);
 * ```
 * @example
 * ```tsx
 * // Example 2: Attach a document event listener with options
 * const elementRef = useRef(document);
 * useEventListener('click', handleClick, elementRef, { capture: true });
 * ```
 * @example
 * ```tsx
 * // Example 3: Attach an element event listener
 * const buttonRef = useRef<HTMLButtonElement>(null);
 * useEventListener('click', handleButtonClick, buttonRef);
 * ```
 */
function useEventListener<
   KW extends keyof WindowEventMap,
   KH extends keyof HTMLElementEventMap & keyof SVGElementEventMap,
   KM extends keyof MediaQueryListEventMap,
   T extends HTMLElement | SVGAElement | MediaQueryList = HTMLElement,
>(
   eventName: KW | KH | KM,
   handler: (
      event:
         | WindowEventMap[KW]
         | HTMLElementEventMap[KH]
         | SVGElementEventMap[KH]
         | MediaQueryListEventMap[KM]
         | Event,
   ) => void,
   element?: React.RefObject<T>,
   options?: boolean | AddEventListenerOptions,
) {
   // Create a ref that stores handler
   const savedHandler = React.useRef(handler)

   React.useLayoutEffect(() => {
      savedHandler.current = handler
   }, [handler])

   React.useEffect(() => {
      // Define the listening target
      const targetElement: T | Window = element?.current ?? window

      if (!targetElement?.addEventListener) return

      // Create event listener that calls handler function stored in ref
      const listener: typeof handler = (event) => {
         savedHandler.current(event)
      }

      targetElement.addEventListener(eventName, listener, options)

      // Remove event listener on cleanup
      return () => {
         targetElement.removeEventListener(eventName, listener, options)
      }
   }, [eventName, element, options])
}

export { useEventListener }
